
import debounce from 'lodash/debounce'
import { Component, Mixins } from 'vue-property-decorator'
import LocaleMixin from '@/mixins/LocaleMixin'
import ToastMixin from '@/mixins/ToastMixin'
import MobileMixin from '@/mixins/MobileMixin'
import UserMixin from '@/mixins/UserMixin'
import ExportModal from '@/components/modals/ExportModal.vue'
import DuplicateProjectModal from '@/components/modals/DuplicateProjectModal.vue'
import PageSizeSelect from '@/components/PageSizeSelect.vue'
import { IMinimalProject, IProject } from '@/types/projects'
import { API_URLS } from '@/utils/helpers'
import { PlatformFeaturesModule } from '@/store/modules/platformFeatures'
import { BvTableFieldArrayWithStickColumn } from '@/types/base'
import GenericTable from "@/components/GenericTable.vue"
import axios from "axios"
import DeleteModal from "@/components/modals/DeleteModal.vue"
import EditButton from '@/components/buttons/EditButton.vue'
import IconButton from '@/components/buttons/IconButton.vue'
import DuplicateButton from '@/components/buttons/DuplicateButton.vue'
import PreviewButton from '@/components/buttons/PreviewButton.vue'
import ProjectLinkButton from '@/components/buttons/ProjectLinkButton.vue'
import CopyURLButton from '@/components/buttons/CopyURLButton.vue'
import DeleteButton from "@/components/buttons/DeleteButton.vue"
import ConfirmModal from '@/components/processing_steps/commons/modals/ConfirmModal.vue'
import BreadcrumbHeader from '@/components/processing_steps/BreadcrumbHeader.vue'
import FormTitle from '@/components/processing_steps/FormTitle.vue'
import StatusFormatterMixin from '@/mixins/StatusFormatterMixin'
import { IFilters } from '@/types/filters'
import OrgaInfoBanner from '@/components/processing_steps/banners/OrgaInfoBanner.vue'
import { IOrgaInfoBannerResponse } from '@/types/processingSteps'

@Component({
  components: {
    DeleteButton,
    DeleteModal,
    GenericTable,
    PageSizeSelect,
    ExportModal,
    DuplicateProjectModal,
    EditButton,
    DuplicateButton,
    PreviewButton,
    ProjectLinkButton,
    CopyURLButton,
    ConfirmModal,
    BreadcrumbHeader,
    FormTitle,
    IconButton,
    OrgaInfoBanner
  },
  mixins: [MobileMixin, UserMixin]
})
export default class AccountProjectList extends Mixins(ToastMixin, MobileMixin, UserMixin, LocaleMixin, StatusFormatterMixin) {
  $refs!: { selectableTable: GenericTable }
  pageSize = 25
  searchString = ''
  tableLoading = true
  duplicateProjectModalOpen = false
  deleteModalOpen = false
  finishModalOpen = false
  archiveModalOpen = false
  pauseModalOpen = false
  resumeModalOpen = false
  deleteErrorMessage = ''
  selectedProject: IProject = null
  selectedProjects: (IProject | IMinimalProject)[] = []
  createDisabled = false

  fields: BvTableFieldArrayWithStickColumn = [
    { key: 'custom_id', label: this.$gettext('Custom ID'), sortable: true },
    { key: 'title', label: this.$gettext('Project name'), sortable: true, stickyColumn: false, tdClass: 'wrap' },
    { key: 'funding_scope.name', label: this.$gettext('Funding scope') },
    { key: 'status', label: 'Status', sortable: true, sortKey: 'mixed_status' },
    { key: 'created_at', label: this.$gettext('Created at'), sortable: true, formatter: this.formatDate, dateRangeFilter: true },
    { key: 'view_actions', label: this.$gettext('Preview') },
    { key: 'fixed_action_col', label: this.$gettext('Actions') }
  ]

  filters: IFilters = {
    status: {
      filterName: 'mixed_status',
      selected: [],
      options: {
        live: this.$gettext('Live'),
        draft: this.$gettext('Draft'),
        review: this.$gettext('In review'),
        paused: this.$gettext('Paused'),
        finished: this.$gettext('Finished'),
        archived: this.$gettext('Archived')
      }
    }
  }

  filterCols = [
    {
      field: 'status',
      filter: 'status',
      name: this.$gettext('Status')
    }
  ]

  customFields = ["status", "view_actions"]

  breadcrumbs = [
    { title: this.$gettext("Projects"), url: "/account/projects-new/", icon: 'projectIcon' }
  ]

  get apiUrl (): string {
    return API_URLS.PROJECTS.ACCOUNT_LIST
  }

  get userIsActivated (): boolean {
    const userInfoDiv = document.querySelector('#user-info')
    return userInfoDiv && userInfoDiv.getAttribute('is_activated') === 'True'
  }

  get description (): string {
    return this.$gettext('Here you can create new projects for which you need specific financing or revise existing financing projects. For example, you can specify here how much money you need for a renovation, a new employee position, a new car, therapy, etc.')
  }

  // todo add type
  getShowButton(project, buttonType: 'edit' | 'pause' |'resume' | 'finish' | 'delete' | 'archive'): boolean {
    if (buttonType === 'edit') {
      return !project.status.finished && !project.status.archived
    } else if (buttonType === 'pause') {
      return project.status.live || project.status.live_for_all
    } else if (buttonType === 'resume') {
      return project.status.paused
    } else if (buttonType === 'finish') {
      return !project.status.archived && !project.status.draft && !project.status.finished && !project.has_active_funding_applications
    } else if (buttonType === 'delete') {
      return project.status.draft
    } else if (buttonType === 'archive') {
      return project.status.finished && !project.has_active_funding_applications
    }
    return false
  }

  onOrgaInfoFetched (showInfoBanner: IOrgaInfoBannerResponse): void {
    this.createDisabled = showInfoBanner.show_orga_not_submitted_banner || showInfoBanner.show_orga_has_no_funding_scopes_banner
  }

  handleSearchChange (event) {
    this.searchString = event.target.value
  }

  openDeleteModal (project: IProject): void {
    this.selectedProject = project
    this.deleteModalOpen = true
    this.deleteErrorMessage = ''
  }

  openFinishProjectModal (project: IProject): void {
    this.selectedProject = project
    this.finishModalOpen = true
  }

  openArchiveProjectModal (project: IProject): void {
    this.selectedProject = project
    this.archiveModalOpen = true
  }

  openPauseProjectModal (project: IProject): void {
    this.selectedProject = project
    this.pauseModalOpen = true
  }

  openResumeProjectModal (project: IProject): void {
    this.selectedProject = project
    this.resumeModalOpen = true
  }

  openDuplicateProjectModal (project?: IProject): void {
    project ? this.selectedProject = project : this.selectedProject = null
    this.duplicateProjectModalOpen = true
  }

  get apiUrlExtraParams () {
    return {
      own: 'true'
    }
  }

  urlPrefix () {
    if (window.location.href.includes("site-admin")) {
      return "/site-admin/"
    } else {
      return "/account/"
    }
  }

  hrefEditContactPerson (data: { item: { slug: string } }) {
    return this.urlPrefix() + "project/" + data.item.slug + "/update/"
  }

  async confirmDelete (): Promise<void> {
    this.deleteErrorMessage = ''
    if (!this.selectedProject) return
    const { title, slug } = this.selectedProject
    axios.delete(API_URLS.PROJECTS.RETRIEVE(slug) + '?own')
      .then(() => {
        this.deleteModalOpen = false
        this.makeToast(
          'success',
          this.$gettext('Success'),
          this.$gettextInterpolate(this.$gettext('Deleted project %{ title }'), { title })
        )
        this.$refs.selectableTable.loadItems()
      })
      .catch(res => {
        if (res.response.data && res.response.data.detail) {
          this.deleteErrorMessage = res.response.data.detail
        }
        this.makeToast(
          'danger',
          this.$gettext('Error'),
          this.$gettext('The project cannot be deleted.')
        )
      })
  }

  async created (): Promise<void> {
    PlatformFeaturesModule.fetchPlatformFeatures()
    this.handleSearchChange = debounce(this.handleSearchChange, 500)
    this.fetchUserProfile()
  }

  async duplicateProject (): Promise<void> {
    if (!this.selectedProjects && !this.selectedProject) return
    const slugList = [this.selectedProject.slug]
    await axios.post(API_URLS.PROJECTS.DUPLICATE, {
      project_slugs: slugList
    }).then(() => {
      this.makeToast(
        'success',
        this.$gettext('Success'),
        this.$gettext('Successfully duplicated')
      )
      this.$emit('close')
      this.$emit('success')
    }).catch(error => {
      this.makeToast(
        'danger',
        this.$gettext('Defunding failed!'),
        ''
      )
      throw Error(error.response)
    })
    this.duplicateProjectModalOpen = false
    this.$refs.selectableTable.loadItems()
  }

  async archiveProject (): Promise<void> {
    const url = API_URLS.PROJECTS.ACCOUNT_CHANGE_STATUS(this.selectedProject.slug)
    const payload = {
      status: 3 // archived
    }
    await axios.patch(url, payload).then(() => {
      this.makeToast(
        'success',
        this.$gettext('Success'),
        this.$gettext('The project has been archived.')
      )
    }).catch(() => {
      this.makeToast(
        'danger',
        this.$gettext('Error'),
        this.$gettext('The project cannot be archived.')
      )
    })
    this.selectedProject = null
    this.$refs.selectableTable.loadItems()
  }

  async changeProjectStatus (queryParam: 'finish' | 'pause'| 'resume' | 'archive'): Promise<void> {
    this.finishModalOpen = false
    this.pauseModalOpen = false
    this.resumeModalOpen = false
    this.archiveModalOpen = false
    if (!this.selectedProject) return
    const url = `/api/v3/projects/${this.selectedProject.slug}/?${queryParam}&own`
    if (queryParam === 'archive') {
      this.archiveProject()
      return
    }
    let successMessage = ''
    let errorMessage = ''
    if (queryParam === 'finish') {
      successMessage = this.$gettext('The project has been finished.')
      errorMessage = this.$gettext('The project cannot be finished.')
    } else if (queryParam === 'pause') {
      successMessage = this.$gettext('The project has been paused.')
      errorMessage = this.$gettext('The project cannot be paused.')
    } else if (queryParam === 'resume') {
      successMessage = this.$gettext('The project has been resumed.')
      errorMessage = this.$gettext('The project cannot be resumed.')
    }
    await axios.put(url)
      .then(() => {
        this.makeToast(
          'success',
          this.$gettext('Success'),
          successMessage
        )
      })
      .catch(() => {
        this.makeToast(
          'danger',
          this.$gettext('Error'),
          errorMessage
        )
      })
    this.selectedProject = null
    this.$refs.selectableTable.loadItems()
  }
}
